export function toLocalISOString(date: Date): string {
  if (isNaN(date.getTime())) {
    return ''
  }
  const pad = (num: number) => String(num).padStart(2, '0')

  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1) // Months are zero-based
  const day = pad(date.getDate())

  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`
}
export function dateDayCeil(date: Date | number): Date {
  const result = new Date(date)
  result.setHours(23, 59, 59, 999)
  return result
}

export function dateDayFloor(date: Date | number): Date {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

const optionsDate: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
const optionsTime: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric', hour12: true }

export function toCorporateDateTime(date: Date, skipTime: boolean = false): string {
  const formattedDate = date.toLocaleDateString('en-US', optionsDate)
  if (skipTime) {
    return formattedDate
  }
  const formattedTime = date.toLocaleTimeString('en-US', optionsTime)
  return `${formattedDate} ${formattedTime}`
}

export function getDefaultTimezoneOffset(date = new Date()): string {
  const offset = date.getTimezoneOffset()
  const sign = offset <= 0 ? '+' : '-'
  const absOffset = Math.abs(offset)
  const hours = Math.abs(Math.floor(absOffset / 60))
  const minutes = Math.abs(absOffset % 60)
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function parseDateWithTimeZone(input: string, timeZoneOffset: string): Date {
  if (/(Z|UTC|GMT)/.test(input)) {
    return new Date(input)
  }
  return new Date(`${input.replace('T', ' ')} UTC${timeZoneOffset}`)
}

export function compareDateStrings(comparableStringA: string, comparableStringB: string) {
  return [
    // year(0), month(1), day(2), hour(3), minute(4), second(5)
    [0, 4],
    [5, 2],
    [8, 1],
    [8, 2],
    [11, 2],
    [14, 2],
    [17, 2]
  ].reduce(
    (memo, [startIndex, length], index) =>
      memo
        ? memo
        : comparableStringA.slice(startIndex, startIndex + length) !==
          comparableStringB.slice(startIndex, startIndex + length)
        ? index + 1
        : 0,
    0
  )
}

export function extendRange(start: number, end: number, percent: number): [number, number] {
  const range = end - start
  const extension = (range * percent) / 2
  return [Math.floor(start - extension), Math.ceil(end + extension)]
}

export type AvailableDateTimeFormats = [
  'longDateTimeFormatter',
  'dateFormatter',
  'timeWithSecondsFormatter',
  'timeWithoutSecondsFormatter',
  'dateTimeForInputFormatter'
]

export function makeDateTimeFormattersForTimezone(timeZone: string) {
  const longDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone
  })

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone
  })

  const timeWithSecondsFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone
  })

  const timeWithoutSecondsFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone
  })

  const dateTimeForInputFormatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23', // Ensures 24-hour format, avoiding AM/PM
    timeZone
  })

  return {
    longDateTimeFormatter,
    dateFormatter,
    timeWithSecondsFormatter,
    timeWithoutSecondsFormatter,
    dateTimeForInputFormatter
  } as Record<AvailableDateTimeFormats[number], Intl.DateTimeFormat>
}

export const MSEC_IN_SEC = 1000
export const MSEC_IN_MIN = MSEC_IN_SEC * 60
export const MSEC_IN_HOUR = MSEC_IN_MIN * 60
export const MSEC_IN_DAY = MSEC_IN_HOUR * 24

export function toTimeParts(timestamp: number) {
  if (Number.isNaN(timestamp) || !Number.isFinite(timestamp) || timestamp < 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }
  }
  const [days, hours, minutes, seconds, milliseconds] = [MSEC_IN_DAY, MSEC_IN_HOUR, MSEC_IN_MIN, MSEC_IN_SEC].reduce(
    (parts, curr) => {
      const acc = parts.pop()
      parts.push(Math.floor(acc / curr))
      parts.push(acc % curr)
      return parts
    },
    [timestamp] as number[]
  )
  return { days, hours, minutes, seconds, milliseconds }
}

export function formatDuration(ms: number, isCompact: boolean = false) {
  const { days, hours, minutes, seconds, milliseconds } = toTimeParts(ms)
  if (days) {
    return {
      text: isCompact ? `${days + (hours >= 12 ? 1 : 0)} d` : pluralize(days + (hours >= 12 ? 1 : 0), 'day'),
      value: days + (hours >= 12 ? 1 : 0),
      unit: 'day',
      isSharp: 0 === hours
    }
  } else if (hours) {
    return {
      text: isCompact ? `${hours + (minutes >= 30 ? 1 : 0)} hr` : pluralize(hours + (minutes >= 30 ? 1 : 0), 'hour'),
      value: hours + (minutes >= 30 ? 1 : 0),
      unit: 'hour',
      isSharp: 0 === minutes
    }
  } else if (minutes) {
    return {
      text: isCompact
        ? `${minutes + (seconds >= 30 ? 1 : 0)} min`
        : pluralize(minutes + (seconds >= 30 ? 1 : 0), 'minute'),
      value: minutes + (seconds >= 30 ? 1 : 0),
      unit: 'minute',
      isSharp: 0 === seconds
    }
  } else if (seconds) {
    return {
      text: isCompact
        ? `${seconds + (milliseconds >= 500 ? 1 : 0)} sec`
        : pluralize(seconds + (milliseconds >= 500 ? 1 : 0), 'second'),
      value: seconds + (milliseconds >= 500 ? 1 : 0),
      unit: 'second',
      isSharp: 0 === milliseconds
    }
  } else {
    return {
      text: isCompact ? '< 1 sec' : 'less than a second',
      value: milliseconds,
      unit: 'millisecond',
      isSharp: true
    }
  }
}

function pluralize(count: number, noun: string) {
  return 1 === count ? `${count} ${noun}` : `${count} ${noun}s`
}

export const cutDate = (source: number | Date) => {
  if (!source) {
    return source
  }
  const date = new Date(source).toISOString()
  const y = date.slice(2, 4)
  const m = date.slice(5, 7)
  const d = date.slice(8, 10)
  return `${m}/${d}/${y}`
}
